/**
 * AuthGuard Component
 * Protects routes that require authentication
 * Handles auth loading state and redirects to sign-in if not authenticated
 * Can be used at layout level to protect all authenticated routes
 */

import React, { useMemo } from 'react';
import { logger } from '../../src/helpers/logger.helper';
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext';
import { Redirect, useGlobalSearchParams, usePathname } from 'expo-router';
import { SignInLoadingScreen } from '../signIn/SignInLoadingScreen';
import { useGuestStore } from '../../src/shared/stores/guestStore';
import { AccountDeletionAccessGate } from './AccountDeletionAccessGate'
import { AgeVerificationGate } from './AgeVerificationGate';
import { PUBLIC_WELCOME_PATH } from '../../src/signIn/constants/publicAuthRoutes.constants';
import {
  appendReturnUrlQuery,
  buildAppReturnUrlForAuthGuard,
  buildPublicSignInHref,
} from '../../src/signIn/helpers/postAuthRedirect.helper';
import {
  AUTHENTICATED_ROUTES,
  ANONYMOUS_ALLOWED_ROUTES,
  PUBLIC_ROUTES,
  OAUTH_CALLBACK_ROUTES,
} from './authGuard.constants';

interface AuthGuardProps {
  children: React.ReactNode
  /**
   * Optional loading component to show while auth is initializing
   * If not provided, shows a simple loading state
   */
  loadingComponent?: React.ReactNode
  /**
   * Optional redirect path (defaults to '/welcome' — the guest-first front door)
   */
  redirectTo?: string
  /**
   * If true, automatically checks the current route and only protects authenticated routes
   * If false, always requires authentication (for page-level usage)
   * @default true
   */
  autoDetectRoute?: boolean
}

/**
 * AuthGuard - Protects authenticated routes
 *
 * When used at layout level with autoDetectRoute=true, it will only protect
 * routes that require authentication, allowing public routes to pass through.
 *
 * Usage at layout level:
 * ```tsx
 * <AuthGuard autoDetectRoute>
 *   <Tabs>...</Tabs>
 * </AuthGuard>
 * ```
 *
 * Usage at page level:
 * ```tsx
 * export default function MyPage() {
 *   return (
 *     <AuthGuard>
 *       <MyPageContent />
 *     </AuthGuard>
 *   );
 * }
 * ```
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  loadingComponent,
  redirectTo = PUBLIC_WELCOME_PATH,
  autoDetectRoute = false,
}) => {
  const { isSignedIn, isAnonymous, isLoaded } = useSupabaseAuth()
  const pathname = usePathname()
  const globalSearch = useGlobalSearchParams()
  const guestHydrated = useGuestStore((s) => s._hasHydrated)

  /** Path + safe query for sign-in returnUrl (pathname alone drops ?code= on WC join links). */
  const authReturnUrl = useMemo(
    () => buildAppReturnUrlForAuthGuard(pathname, globalSearch as Record<string, unknown>),
    [pathname, globalSearch],
  )

  // Get the current route name from pathname
  // pathname will be like '/app/index' or '/app/invite/token-value' (auth entry is /sign-in at site root)
  // Since AuthGuard is inside app/app/_layout.tsx, the first segment is often `app`; on some web builds it is omitted.
  const currentRoute = useMemo(() => {
    if (!autoDetectRoute) {
      return null // Don't detect route when autoDetectRoute is false
    }
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length === 0) {
      return 'index'
    }
    if (segments[0] === 'app') {
      return segments[1] ?? 'index'
    }
    return segments[0] ?? 'index'
  }, [autoDetectRoute, pathname])

  // Determine route type
  const routeType = useMemo(() => {
    if (!autoDetectRoute || !currentRoute) {
      return 'unknown' // When not auto-detecting, treat as unknown
    }

    if (AUTHENTICATED_ROUTES.includes(currentRoute)) {
      return 'authenticated'
    }
    if (PUBLIC_ROUTES.includes(currentRoute)) {
      return 'public'
    }
    if (OAUTH_CALLBACK_ROUTES.includes(currentRoute)) {
      return 'oauth_callback' // Special case: always allow through
    }
    // Unknown route - not in either list
    return 'unknown'
  }, [autoDetectRoute, currentRoute])

  logger.debug('[AuthGuard]', { isLoaded, guestHydrated, isSignedIn, isAnonymous, pathname, currentRoute, routeType })

  // Show loading state while auth or guest store is initializing
  // Guest store hydration prevents sign-in flash on mobile cold start
  if (!isLoaded || !guestHydrated) {
    if (loadingComponent) {
      return <>{loadingComponent}</>
    }

    // Default loading state - uses consistent SignInLoadingScreen
    return <SignInLoadingScreen />
  }

  // OAuth callback routes: always allow through - they handle their own redirects
  if (routeType === 'oauth_callback') {
    return <>{children}</>
  }

  // Handle public routes: if already signed in, redirect to app index
  // Exception: landing page handles its own redirects
  // Exception: tutorial page is allowed for authenticated users (TutorialGuard handles logic)
  if (routeType === 'public' && isSignedIn && currentRoute !== 'landing' && currentRoute !== 'tutorial') {
    return <Redirect href="/app/" />;
  }

  // Handle authenticated routes: if not signed in, send to the Welcome front door (not straight to
  // the sign-in form). Carry the returnUrl so Welcome's "I already have an account" forwards a deep
  // link on to sign-in; "Get Started" (guest) ignores it. See specs/guest-first-onboarding (AC-4b).
  if (routeType === 'authenticated' && !isSignedIn) {
    return (
      <Redirect href={appendReturnUrlQuery(PUBLIC_WELCOME_PATH, authReturnUrl) as `/${string}`} />
    )
  }

  // Anonymous users: restrict to allowed routes only
  if (routeType === 'authenticated' && isSignedIn && isAnonymous) {
    if (!ANONYMOUS_ALLOWED_ROUTES.includes(currentRoute ?? '')) {
      return (
        <Redirect
          href={buildPublicSignInHref({ signup: true, returnUrl: authReturnUrl }) as `/${string}`}
        />
      )
    }
  }

  // Handle unknown routes: redirect to app index if signed in, sign-in if not
  if (routeType === 'unknown') {
    if (isSignedIn) {
      return <Redirect href="/app/" />
    }
    return <Redirect href={redirectTo} />
  }

  // When autoDetectRoute is false, always require auth
  if (!autoDetectRoute) {
    if (!isSignedIn) {
      return <Redirect href={redirectTo} />
    }
    return (
      <AccountDeletionAccessGate>
        <AgeVerificationGate>{children}</AgeVerificationGate>
      </AccountDeletionAccessGate>
    )
  }

  if (routeType === 'authenticated' && isSignedIn) {
    return (
      <AccountDeletionAccessGate>
        <AgeVerificationGate>{children}</AgeVerificationGate>
      </AccountDeletionAccessGate>
    )
  }

  // Render children for all other cases (public routes, etc.)
  return <>{children}</>
}
